import type { RangeFrame } from "@auto-pools/core";
import type { ScalePercents } from "../components/PriceScale";

/** Geometry and percentages for one orientation, ready to spread onto the compact scale. */
export interface FrameScale {
  min: string;
  max: string;
  entry: string;
  current: string | null;
  lowerStop?: string;
  upperStop?: string;
  percents: ScalePercents;
}

/**
 * Reads one orientation off a `RangeFrame`. Prices and percentages leave together, so a flip can
 * never pair one quote's bounds with the other quote's numbers — the failure the block is being
 * rebuilt to remove. `stops` drops a side the caller does not draw (a one-sided or disarmed
 * policy): a trigger price that is not in force must not appear as a mark, nor lend its percentage.
 * It is stated in CANONICAL sides, the way `exitPolicy.side` is, because that is the only side the
 * caller knows; inversion reverses the axis, so in a flipped frame the canonical lower stop is the
 * upper mark and the permission has to travel with it — otherwise ⇄ makes a one-sided stop vanish.
 */
export function frameScale(
  frame: RangeFrame,
  stops: { lower: boolean; upper: boolean } = { lower: true, upper: true },
): FrameScale | null {
  const start = frame.marks.find((mark) => mark.role === "range-start");
  const end = frame.marks.find((mark) => mark.role === "range-end");
  if (!start || !end) return null;
  const allowed = frame.inverted ? { lower: stops.upper, upper: stops.lower } : stops;
  const stopOn = (side: "lower" | "upper") =>
    allowed[side]
      ? (frame.marks.find((m) => m.role === "auto-exit-stop" && m.side === side) ?? null)
      : null;
  const lower = stopOn("lower");
  const upper = stopOn("upper");
  return {
    min: start.price,
    max: end.price,
    entry: frame.entryPrice,
    current: frame.markerPrice,
    lowerStop: lower?.price,
    upperStop: upper?.price,
    percents: {
      lower: start.percent,
      upper: end.percent,
      lowerStop: lower?.percent ?? null,
      upperStop: upper?.percent ?? null,
      lowerStopAway: lower?.awayPercent ?? null,
      upperStopAway: upper?.awayPercent ?? null,
      marker: frame.markerPercent,
      beyond: frame.beyond,
    },
  };
}
